import { useState, useMemo } from "react";
import { ResponsiveModal } from "@/ui/ResponsiveModal";
import { Button } from "@/ui/base/button";
import { Input } from "@/ui/base/input";
import { Label } from "@/ui/base/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/base/select";
import { Loader2 } from "lucide-react";
import type { DocArticle } from "@/lib/useDocs";

const ICON_OPTIONS = [
  { value: "FileText", label: "Documento" },
  { value: "BookOpen", label: "Livro" },
  { value: "Settings", label: "Configurações" },
  { value: "Shield", label: "Segurança" },
  { value: "Database", label: "Banco de Dados" },
  { value: "Code", label: "Código" },
  { value: "Layers", label: "Camadas" },
  { value: "Map", label: "Mapa" },
  { value: "Users", label: "Usuários" },
  { value: "Workflow", label: "Fluxo" },
  { value: "BarChart3", label: "Gráfico" },
  { value: "Calculator", label: "Calculadora" },
];

interface NewArticleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  articles: DocArticle[];
  onSubmit: (data: {
    titulo: string;
    slug: string;
    parent_id?: string | null;
    icone?: string;
    ordem?: number;
    conteudo_markdown?: string;
  }) => Promise<void>;
  saving?: boolean;
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function NewArticleDialog({
  open,
  onOpenChange,
  articles,
  onSubmit,
  saving,
}: NewArticleDialogProps) {
  const [titulo, setTitulo] = useState("");
  const [slug, setSlug] = useState("");
  const [parentId, setParentId] = useState<string>("none");
  const [icone, setIcone] = useState("FileText");
  const [slugEdited, setSlugEdited] = useState(false);

  const rootArticles = useMemo(
    () => articles.filter((a) => !a.parent_id).sort((a, b) => a.ordem - b.ordem),
    [articles]
  );

  const nextOrdem = useMemo(() => {
    if (parentId === "none") {
      return Math.max(0, ...articles.filter((a) => !a.parent_id).map((a) => a.ordem)) + 1;
    }
    return Math.max(0, ...articles.filter((a) => a.parent_id === parentId).map((a) => a.ordem)) + 1;
  }, [articles, parentId]);

  const handleTituloChange = (value: string) => {
    setTitulo(value);
    if (!slugEdited) setSlug(slugify(value));
  };

  const handleSubmit = async () => {
    if (!titulo.trim() || !slug.trim()) return;
    await onSubmit({
      titulo: titulo.trim(),
      slug: slug.trim(),
      parent_id: parentId === "none" ? null : parentId,
      icone,
      ordem: nextOrdem,
      conteudo_markdown: "",
    });
    setTitulo("");
    setSlug("");
    setParentId("none");
    setIcone("FileText");
    setSlugEdited(false);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Novo Artigo"
      description="Preencha os dados do novo artigo de documentação."
      desktopMaxWidth="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="h-11 sm:h-10 w-full sm:w-auto">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!titulo.trim() || !slug.trim() || saving} className="h-11 sm:h-10 w-full sm:w-auto">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Criar Artigo
          </Button>
        </>
      }
    >
      <div className="space-y-4 py-2">
        <div className="space-y-1.5">
          <Label htmlFor="titulo" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Título *
          </Label>
          <Input
            id="titulo"
            value={titulo}
            onChange={(e) => handleTituloChange(e.target.value)}
            placeholder="Ex: Guia de Integração"
            className="h-11 sm:h-10"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="slug" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Slug *
          </Label>
          <Input
            id="slug"
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setSlugEdited(true); }}
            placeholder="guia-de-integracao"
            className="h-11 sm:h-10"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Artigo Pai</Label>
          <Select value={parentId} onValueChange={setParentId}>
            <SelectTrigger className="h-11 sm:h-10"><SelectValue placeholder="Nenhum (raiz)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum (raiz)</SelectItem>
              {rootArticles.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.titulo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ícone</Label>
          <Select value={icone} onValueChange={setIcone}>
            <SelectTrigger className="h-11 sm:h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ICON_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </ResponsiveModal>
  );
}
